import { ReactNode } from "react";
import { AppSidebar } from "@/ui/components/layout/app-sidebar";
import { useAppNav } from "@/contexts/AppNavContext";
import { CreateModalProvider, useCreateModal } from "@/contexts/CreateModalContext";
import {
  RANGER_PANEL_WIDTH_CLASS,
  useRangerPanel,
  useRegisterRangerAppShell,
} from "@/contexts/RangerPanelContext";
import { CreateTripModal } from "@/modules/trips";
import { cn } from "@/shared/utils";

interface AppShellProps {
  children: ReactNode;
  className?: string;
}

function AppShellInner({ children, className = "" }: AppShellProps) {
  const { open, closeCreateModal } = useCreateModal();
  const { expanded } = useAppNav();
  const { isOpen: rangerOpen } = useRangerPanel();
  useRegisterRangerAppShell();

  return (
    <div className={`min-h-screen bg-earth-50 flex flex-row ${className}`}>
      <AppSidebar />
      {/* Ranger itself lives above the routes so its chat survives page changes. This keeps its room. */}
      <div
        aria-hidden
        className={cn(
          "hidden md:block flex-shrink-0 transition-[width] duration-200 ease-out",
          rangerOpen ? RANGER_PANEL_WIDTH_CLASS : "w-0"
        )}
      />
      <main
        className={cn(
          "flex-1 min-w-0 flex flex-col pb-16 md:pb-0 min-h-screen",
          expanded ? "md:ml-60" : "md:ml-14"
        )}
      >
        <div className="flex-1 min-w-0 min-h-0">{children}</div>
      </main>
      <CreateTripModal open={open} onOpenChange={(isOpen) => !isOpen && closeCreateModal()} />
    </div>
  );
}

/**
 * App shell for authenticated app: left sidebar + main content area.
 * No top header. Supports future three-column layout (list | center | panel) inside main.
 */
export function AppShell({ children, className = "" }: AppShellProps) {
  return (
    <CreateModalProvider>
      <AppShellInner className={className}>{children}</AppShellInner>
    </CreateModalProvider>
  );
}
